import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import DataGrid, { Column, Paging, FilterRow, HeaderFilter, Selection, FilterPanel, StateStoring } from 'devextreme-react/data-grid';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import moment from 'moment-timezone';
import hash from 'hash.js';
import AssetsService from "../../api/assets.service";
import TenantService from "../../api/tenant.service";
import FloorplanPopupMultiple from '../components/floorplanMultiple2';
import FloorplanGeo2 from '../components/floorplanGeo';
import Toast from 'components/Toast';

const App = forwardRef(function App({ assetIds, assetColorMap, startDate, endDate, selectedSpace, floorplanGeo, isPoll, spaces }, ref) {
  console.log("selectedSpace asset", selectedSpace)
  const gridRef = useRef(null);
  const [data, setData] = useState([]);
  const [tags, setTags] = useState([]);
  const [showMyFloorplan, setShowMyFloorplan] = useState(false);
  const [showMyFloorplan2, setShowMyFloorplan2] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loadingText, setLoadingText] = useState('Loading...');
  const [hoveredCoordinates, setHoveredCoordinates] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [lines, setLines] = useState(true);
  // const lastFetchParams = useRef("");
  const syncLock = useRef("");
  const dataRef = useRef([]);
  const [palette, setPalette] = useState([]);

  // Sync ref so the fetcher always knows what's currently in the table
  useEffect(() => {
    dataRef.current = data;
    console.log("data",data)
  }, [data]);

  const [toastConfig, setToastConfig] = useState({ isVisible: false, type: 'info', message: '' });

  const generateDeterministicId = (record) => {
    return hash.sha256().update(`${record.asset}-${record.time}-${record.motion}`).digest('hex');
  };

  const processNewData = useCallback((history) => {
    if (!history || history.length === 0) {
      return { cleaned: [], markers: [] };
    }

    let markers = [];
    const cleaned = history.map(item => {
      // console.log("item@@", item)
      const id = generateDeterministicId(item);
      if (item.space?.localCoordinates) {
        markers.push({
          parentId: id,
          id: item.space.id,
          asset: item.asset,
          serialNumber: moment(item.time).format("YYYY-MM-DD HH:mm:ss"),
          spaceCoordinates: {
            x: item.space.localCoordinates.x,
            y: item.space.localCoordinates.y
          },
          // Now uses the palette index instead of random

        });
      }
      return { ...item, id, locationType: item.local_coordinates ? "Local" : "Geo" };
    });
    return { cleaned, markers };
  }, []); // Re-run when palette loads

  const fetchData = useCallback(async () => {
    // --- THE GATEKEEPER ---
    const fingerprint = `${selectedSpace}-${startDate}-${endDate}-${isPoll}-${assetIds.join(',')}`;

    if (syncLock.current === fingerprint) {
      return;
    }
    syncLock.current = fingerprint;

    if (!selectedSpace) {
      setLoadingText('No Data');
      return;
    }

    // 1. Decouple the 'From' dates
    // tableFrom moves forward during polling
    // floorplanFrom stays locked to the original startDate to keep history visible
    let tableFrom;
    const floorplanFrom = moment(startDate).format();

    if (isPoll && dataRef.current.length > 0) {
      const latestEntry = dataRef.current[0].time;
      tableFrom = moment(latestEntry).format();
    } else {
      if (!startDate) return;
      tableFrom = moment(startDate).format();
      setLoadingText('Loading...');
    }

    const to = moment(endDate).format();

    try {
      let history = [];
      let floorplanHistory = [];
      const isGeoMap = selectedSpace === "6a21bf9e-fc6b-4bec-9f5c-8f8297772091";

      if (isGeoMap) {
        // For GeoMap, we usually want the full snapshot for the floorplan component anyway
        const res = await AssetsService.locationHistoryGeo(assetIds, tableFrom, to);
        history = res?.data?.data || [];
        floorplanHistory = history;
      } else {
        // 2. Fetch table data with the sliding window (incremental)
        console.log("spaces", spaces)
        const res = await AssetsService.locationHistoryMultiple(assetIds, tableFrom, to, spaces, "asset");
        console.log(res?.data?.data.history.length)

        // 3. Fetch floorplan data with the FIXED window (original startDate)
        // This ensures markers stay on the map even if no NEW movement occurred in the last poll
        const res2 = await AssetsService.locationHistoryMultiple(assetIds, floorplanFrom, to, selectedSpace, "asset");

        history = res?.data?.data.history || [];
        floorplanHistory = res2?.data?.data.history || [];
      }

      // --- PROCESS TABLE DATA ---
      if (history.length > 0) {
        const { cleaned } = processNewData(history);
        setData(prevData => {
          const combined = isPoll ? [...cleaned, ...prevData] : cleaned;
          const uniqueMap = new Map();
          combined.forEach(item => uniqueMap.set(item.id, item));
          return Array.from(uniqueMap.values()).sort((a, b) => new Date(b.time) - new Date(a.time));
        });
        setLoadingText('');
      } else if (!isPoll) {
        setData([]);
        setLoadingText('No Data');
      }

      // --- PROCESS FLOORPLAN DATA ---
      if (floorplanHistory.length > 0) {
        const { markers } = processNewData(floorplanHistory);

        // 4. IMPORTANT: Since floorplanFrom is the FULL range, 
        // we don't 'append' anymore; we just set the snapshot.
        setTags(markers);

        setShowMyFloorplan(true);
        setShowMyFloorplan2(true);
      } else {
        // Only clear if it's a fresh load (not a poll) or if history is truly empty
        if (!isPoll) setTags([]);
      }

    } catch (err) {
      console.error("Fetch error:", err);
      if (!isPoll) setLoadingText("Error loading data");
    }
  }, [assetIds, startDate, endDate, selectedSpace, isPoll, processNewData, spaces]);

  // const fetchData = useCallback(async () => {
  //   // --- THE GATEKEEPER ---
  //   // Generate a unique string based on the current props
  //   const fingerprint = `${selectedSpace}-${startDate}-${endDate}-${isPoll}-${assetIds.join(',')}`;

  //   // If the fingerprint matches the last time we successfully ran this, BAIL OUT.
  //   // This stops the 2nd "Now" render from ever executing the code below.
  //   if (syncLock.current === fingerprint){
  //     console.log("syncloc")
  //     setLoadingText('No Data');
  //     return;
  //   } 
  //   syncLock.current = fingerprint;
  //   if (!selectedSpace){
  //     console.log("syncloc2")
  //     setLoadingText('No Data');
  //     return;
  //   } 

  //   let from;
  //   // If parent says isPoll is active, we only fetch what's missing since the last record
  //   if (isPoll && dataRef.current.length > 0) {
  //     const latestEntry = dataRef.current[0].time;
  //     from = moment(latestEntry).format();
  //   } else {
  //     if (!startDate) return;
  //     from = moment(startDate).format();
  //     setLoadingText('Loading...');
  //   }

  //   const to = moment(endDate).format();

  //   try {
  //     let history = [];
  //     let floorplanHistory = []
  //     const isGeoMap = selectedSpace === "6a21bf9e-fc6b-4bec-9f5c-8f8297772091";
  //     if (isGeoMap) {
  //       const res = await AssetsService.locationHistoryGeo(assetIds[0], from, to);
  //       history = res?.data?.data || [];
  //     } else {
  //       console.log("assetIds, from, to, spaces",assetIds, from, to, spaces)
  //       const res = await AssetsService.locationHistoryMultiple(assetIds, from, to, spaces, "asset");
  //       const res2 = await AssetsService.locationHistoryMultiple(assetIds, from, to, selectedSpace, "asset");
  //       // history = res?.data?.data || []; //@@@ new
  //       history = res?.data?.data.history || [];
  //       floorplanHistory = res2?.data?.data.history || [];
  //     }
  //     // --- PROCESS TABLE DATA ---
  //     console.log("history", history)
  //     console.log("floorplanHistory", floorplanHistory)
  //     if (history.length > 0) {

  //       const { cleaned } = processNewData(history);
  //       setData(prevData => {
  //         const combined = isPoll ? [...cleaned, ...prevData] : cleaned;
  //         const uniqueMap = new Map();
  //         combined.forEach(item => uniqueMap.set(item.id, item));
  //         return Array.from(uniqueMap.values()).sort((a, b) => new Date(b.time) - new Date(a.time));
  //       });
  //       setLoadingText('');
  //     } else if (!isPoll) {
  //       setData([]);
  //       setLoadingText('No Data');
  //     }

  //     // --- PROCESS FLOORPLAN DATA ---
  //     if (floorplanHistory.length > 0) {
  //       const { markers } = processNewData(floorplanHistory);
  //       setTags(prevTags => {
  //         const combined = isPoll ? [...markers, ...prevTags] : markers;
  //         const uniqueMap = new Map();
  //         combined.forEach(item => uniqueMap.set(item.parentId, item));
  //         return Array.from(uniqueMap.values()).sort((a, b) => new Date(a.serialNumber) - new Date(b.serialNumber));
  //       });
  //       setShowMyFloorplan(true);
  //       setShowMyFloorplan2(true);
  //     } else {
  //       setTags([]);
  //       // We don't hide the floorplan necessarily, just clear the dots
  //     }

  //   } catch (err) {
  //     console.error("Fetch error:", err);
  //     if (!isPoll) setLoadingText("Error loading data");
  //   }
  // }, [assetIds, startDate, endDate, selectedSpace, isPoll, processNewData]);


  useEffect(() => {
    // 1. Set a timer to run fetchData after a short delay (e.g., 250ms)
    const handler = setTimeout(() => {
      fetchData();
    }, 250);
    // 2. CLEANUP: If the props change again before 250ms is up,
    // this clears the old timer and starts a new one.
    // This effectively "waits until props settle."
    return () => {
      clearTimeout(handler);
    };
  }, [fetchData]); // This triggers whenever fetchData changes (which is when props change)

  function exportGrid() {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Asset History");
    worksheet.columns = [
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 25 },
      { width: 20 },
    ];
    exportDataGrid({
      component: gridRef.current.instance(),
      worksheet,
      keepColumnWidths: false,
      topLeftCell: { row: 1, column: 1 },
      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell.rowType === 'data' && gridCell.column.dataField === 'time') {
          excelCell.value = moment(gridCell.value).format('YYYY-MM-DD HH:mm:ss');
        }
      },
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `AssetHistory-${moment().format('YYYY-MM-DD-HH-mm-ss')}.xlsx`);
      });
    });
  }

  useImperativeHandle(ref, () => ({ exportGrid }));

  const handleSelectionChanged = useCallback(({ selectedRowKeys, selectedRowsData }) => {
    setSelectedRowKeys(selectedRowKeys);
    if (selectedRowsData.length > 0) setSelectedRow(selectedRowsData[0]);
  }, []);

  // const handleCellHover = useCallback((e) => {
  //   console.log("e.data",e.data)
  //   if (e.eventType === 'mouseover' && e.data?.space?.localCoordinates) {
      
  //     if (e.data.space.id === selectedSpace || e.data.space.space_head_id === selectedSpace){
  //       setHoveredCoordinates(e.data.space.localCoordinates);
  //     } else {
  //       console.log("e.data.space.id", e.data.space.id)
  //       console.log("selectedSpace", selectedSpace)
  //       setHoveredCoordinates(null);
  //     }
  //   } else {
  //     setHoveredCoordinates(null);
  //   }
  // }, [selectedSpace]);

  const handleCellHover = useCallback((e) => {
    if (e.eventType === 'mouseover') {
        const isGeoMap = selectedSpace === "6a21bf9e-fc6b-4bec-9f5c-8f8297772091";

        if (isGeoMap && e.data?.geo_coordinates) {
          console.log("here",e.data.geo_coordinates)
            setHoveredCoordinates(e.data.geo_coordinates);
        } else if (!isGeoMap && e.data?.space?.localCoordinates) {
            if (e.data.space.id === selectedSpace || e.data.space.space_head_id === selectedSpace) {
                setHoveredCoordinates(e.data.space.localCoordinates);
            } else {
                setHoveredCoordinates(null);
            }
        } else {
            setHoveredCoordinates(null);
        }
    } else {
        setHoveredCoordinates(null);
    }
}, [selectedSpace]);

  const renderFloorplan = () => {
    if (!selectedSpace) return null;
    if (selectedSpace === "6a21bf9e-fc6b-4bec-9f5c-8f8297772091") {
      return <FloorplanGeo2 data={data} selectedRow={selectedRow} hoveredCoordinates={hoveredCoordinates} assetColorMap={assetColorMap}/>;
    }
    if (tags.length === 0) {
      return null;
    } else {
      console.log("selectedSpace to floorplan", selectedSpace)
      return (
        <FloorplanPopupMultiple
          hoveredCoordinates={hoveredCoordinates}
          spaceId={selectedSpace}
          coordinates={tags}
          assetColorMap={assetColorMap}
        />
      );
    }
  };

  return (
    <div className='assetContainer'>
      <Toast setParentToastConfig={setToastConfig} toastBody={toastConfig} />
      <section className='column_two'>
        <DataGrid
          ref={gridRef}
          height={(showMyFloorplan && showMyFloorplan2) ? 'calc(100vh - 303px)' : 'calc(100vh - 329px)'}
          dataSource={data}
          keyExpr="id"
          showBorders={true}
          focusedRowEnabled={true}
          selectedRowKeys={selectedRowKeys}
          onSelectionChanged={handleSelectionChanged}
          onCellHoverChanged={handleCellHover}
          noDataText={loadingText}
          repaintChangesOnly={true}
          allowColumnResizing={true}
        >
          <StateStoring enabled={true} type="localStorage" storageKey="myAssetGridState" />
          <Selection mode={floorplanGeo ? "single" : "multiple"} showCheckBoxesMode="none" />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <FilterPanel visible={true} />
          <Paging enabled={false} />
          <Column dataField="time" width={"170px"} caption="Date" dataType="datetime" format="yyyy-MM-dd HH:mm:ss" sortOrder="desc" allowFiltering={false} />
          <Column dataField="asset" caption="Asset" />
          {!floorplanGeo && <Column dataField="space.name" caption="Space" />}
          {!floorplanGeo && <Column dataField="motion" caption="Event" width={"30%"} />}
          {!floorplanGeo && <Column dataField="distance" caption="Distance [m]" />}
        </DataGrid>
        <div className={true ? 'showFloorplan-asset' : 'hideFloorplan'}>
          {renderFloorplan()}
        </div>
      </section>
    </div>
  );
});

export default App;